import {
  ListChecks,
  Waypoints,
  Target,
  AlertTriangle,
  ShieldAlert,
  Trash2,
} from 'lucide-react';
import { Badge, Button } from '../../ui';
import {
  formatTime,
  hasBlocker,
} from '../../features/dailyUpdates/dailyUpdateModel';
import type { DailyUpdateSummary } from '../../features/dailyUpdates/types';

export interface DailyUpdateCardProps {
  update: DailyUpdateSummary;
  storyTitle?: string;
  workstreamTitle?: string;
  goalTitle?: string;
  onOpenWorkstream?: (workstreamId: string) => void;
  onOpenGoal?: (goalId: string) => void;
  /** Offer to convert the free-text blocker into a structured Blocker. */
  onConvertBlocker?: (update: DailyUpdateSummary) => void;
  onDelete?: (update: DailyUpdateSummary) => void;
}

/** A labeled text line within an update. */
function Line({ label, value }: { label: string; value?: string }) {
  if (!value?.trim()) return null;
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
        {label}
      </p>
      <p className="mt-0.5 whitespace-pre-wrap text-sm text-zinc-200">{value}</p>
    </div>
  );
}

/**
 * A single daily-update card in the feed. Shows the story it's about, the
 * today/yesterday/result lines, and — if a blocker was noted — a highlighted
 * blocker block with a one-click "Convert to blocker" action.
 */
export function DailyUpdateCard({
  update,
  storyTitle,
  workstreamTitle,
  goalTitle,
  onOpenWorkstream,
  onOpenGoal,
  onConvertBlocker,
  onDelete,
}: DailyUpdateCardProps) {
  const blocked = hasBlocker(update);

  return (
    <div className="rounded-xl border border-surface-border bg-surface-1 p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-1.5">
          {update.storyId ? (
            <Badge tone="neutral">
              <ListChecks className="h-3 w-3" /> {storyTitle ?? 'Story'}
            </Badge>
          ) : (
            <Badge tone="neutral">General update</Badge>
          )}
          {update.workstreamId && (
            <button
              type="button"
              onClick={() => update.workstreamId && onOpenWorkstream?.(update.workstreamId)}
              disabled={!onOpenWorkstream}
            >
              <Badge tone="info">
                <Waypoints className="h-3 w-3" /> {workstreamTitle ?? 'Workstream'}
              </Badge>
            </button>
          )}
          {update.goalId && (
            <button
              type="button"
              onClick={() => update.goalId && onOpenGoal?.(update.goalId)}
              disabled={!onOpenGoal}
            >
              <Badge tone="accent">
                <Target className="h-3 w-3" /> {goalTitle ?? 'Goal'}
              </Badge>
            </button>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className="text-xs text-zinc-500">{formatTime(update.createdAt)}</span>
          {onDelete && (
            <button
              type="button"
              onClick={() => onDelete(update)}
              aria-label="Delete update"
              className="rounded-md p-1 text-zinc-500 transition-colors hover:bg-surface-3 hover:text-red-300"
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      <div className="mt-3 space-y-2.5">
        <Line label="Today / next" value={update.today} />
        <Line label="Yesterday" value={update.yesterday} />
        <Line label="Result / learning" value={update.resultLearning} />

        {blocked && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-red-300">
                  <AlertTriangle className="h-3 w-3" aria-hidden="true" /> Blocker
                </p>
                <p className="mt-0.5 whitespace-pre-wrap text-sm text-red-100">
                  {update.blocker}
                </p>
              </div>
              {onConvertBlocker && (
                <Button
                  size="sm"
                  variant="secondary"
                  leftIcon={<ShieldAlert className="h-3.5 w-3.5" />}
                  className="shrink-0"
                  onClick={() => onConvertBlocker(update)}
                >
                  Convert to blocker
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DailyUpdateCard;
